import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Slider
} from '@mui/material';
import {
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Image as ImageIcon,
  People as PeopleIcon,
  Payment as PaymentIcon,
  Verified as VerifiedIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const PushNotification = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [targetUsers, setTargetUsers] = useState([]);
  const [targetCount, setTargetCount] = useState(0);
  const [notificationStats, setNotificationStats] = useState(null);
  const [notificationHistory, setNotificationHistory] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    image_url: '',
    notification_type: 'general',
    target_type: 'all_users',
    deep_link: '',
    promo_code: '',
    schedule_date: '',
    expiry_days: 7
  });
  
  const [filters, setFilters] = useState({
    search: '',
    subscription_status: 'all',
    has_active_package: 'all',
    gender: 'all',
    age_from: 18,
    age_to: 60
  });
  
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchHistory();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/notifications/stats');
      setNotificationStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/notifications/history');
      setNotificationHistory(response.data.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchTargetUsers = async () => {
    setLoading(true);
    try {
      const response = await api.post('/notifications/get-target-users', {
        ...filters,
        target_type: formData.target_type
      });
      setTargetUsers(response.data.data);
      setTargetCount(response.data.total);
      toast.success(`Found ${response.data.total} users with ${response.data.total_with_tokens} active devices`);
    } catch (error) {
      toast.error('Failed to fetch target users');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async (isScheduled = false) => {
    if (!formData.title || !formData.body) {
      toast.error('Please enter title and body');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        target_type: formData.target_type,
        user_ids: formData.target_type === 'selected_users' ? selectedUserIds : [],
        schedule_date: isScheduled ? formData.schedule_date : null
      };

      const response = await api.post('/notifications/send', payload);
      
      if (response.data.success) {
        toast.success(isScheduled ? 'Notification scheduled successfully' : 'Notification sent successfully');
        setFormData({
          title: '',
          body: '',
          image_url: '',
          notification_type: 'general',
          target_type: 'all_users',
          deep_link: '',
          promo_code: '',
          schedule_date: '',
          expiry_days: 7
        });
        setSelectedUserIds([]);
        fetchStats();
        fetchHistory();
        setScheduleDialogOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const targetOptions = [
    { value: 'all_users', label: 'All Users', icon: <PeopleIcon />, desc: 'Send to all registered users' },
    { value: 'paid_users', label: 'Paid Users', icon: <PaymentIcon />, desc: 'Users with active paid subscription' },
    { value: 'free_users', label: 'Free Users', icon: <PeopleIcon />, desc: 'Users without active subscription' },
    { value: 'active_subscription', label: 'Active Subscription', icon: <VerifiedIcon />, desc: 'Users with active package' },
    { value: 'expiring_soon', label: 'Expiring Soon', icon: <ScheduleIcon />, desc: 'Subscription expiring in 7 days' },
    { value: 'expired_subscription', label: 'Expired Subscription', icon: <ScheduleIcon />, desc: 'Users with expired subscription' },
    { value: 'verified_users', label: 'Verified Users', icon: <VerifiedIcon />, desc: 'Email/Phone verified users' },
    { value: 'unverified_users', label: 'Unverified Users', icon: <VerifiedIcon />, desc: 'Not yet verified users' },
    { value: 'inactive_users', label: 'Inactive Users', icon: <PeopleIcon />, desc: 'Not logged in for 30+ days' }
  ];

  const notificationTypes = [
    { value: 'general', label: 'General', color: '#1976d2' },
    { value: 'promo', label: 'Promotional', color: '#e53935' },
    { value: 'renewal', label: 'Renewal Reminder', color: '#ed6c02' },
    { value: 'package_expiry', label: 'Package Expiry', color: '#f57c00' },
    { value: 'match', label: 'Match Alert', color: '#4caf50' },
    { value: 'message', label: 'Message Alert', color: '#9c27b0' },
    { value: 'update', label: 'App Update', color: '#00bcd4' }
  ];

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        Push Notification Manager
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h6">Total Sent</Typography>
              <Typography variant="h3">{notificationStats?.total_notifications || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h6">Successful</Typography>
              <Typography variant="h3">{notificationStats?.total_successful || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h6">Avg Open Rate</Typography>
              <Typography variant="h3">0%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h6">Active Devices</Typography>
              <Typography variant="h3">0</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Send Notification" />
          <Tab label="Target Users" />
          <Tab label="History" />
          <Tab label="Analytics" />
        </Tabs>

        {/* Send Notification Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Compose Notification</Typography>
                  
                  <TextField
                    fullWidth
                    label="Title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    margin="normal"
                    placeholder="Enter notification title"
                  />
                  
                  <TextField
                    fullWidth
                    label="Message Body"
                    value={formData.body}
                    onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                    margin="normal"
                    multiline
                    rows={4}
                    placeholder="Enter notification message"
                  />
                  
                  <TextField
                    fullWidth
                    label="Image URL (Optional)"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    margin="normal"
                    placeholder="https://example.com/image.jpg"
                  />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Notification Type</InputLabel>
                        <Select
                          value={formData.notification_type}
                          label="Notification Type"
                          onChange={(e) => setFormData({ ...formData, notification_type: e.target.value })}
                        >
                          {notificationTypes.map(type => (
                            <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Target Audience</InputLabel>
                        <Select
                          value={formData.target_type}
                          label="Target Audience"
                          onChange={(e) => setFormData({ ...formData, target_type: e.target.value })}
                        >
                          {targetOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  
                  <TextField
                    fullWidth
                    label="Deep Link (Optional)"
                    value={formData.deep_link}
                    onChange={(e) => setFormData({ ...formData, deep_link: e.target.value })}
                    margin="normal"
                    placeholder="app://screen/params"
                  />
                  
                  <TextField
                    fullWidth
                    label="Promo Code (Optional)"
                    value={formData.promo_code}
                    onChange={(e) => setFormData({ ...formData, promo_code: e.target.value })}
                    margin="normal"
                    placeholder="SAVE20"
                  />
                  
                  <Box display="flex" gap={2} mt={3}>
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={() => handleSendNotification(false)}
                      disabled={loading || !formData.title || !formData.body}
                      fullWidth
                      sx={{
                        background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)' }
                      }}
                    >
                      Send Now
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ScheduleIcon />}
                      onClick={() => setScheduleDialogOpen(true)}
                      disabled={!formData.title || !formData.body}
                      fullWidth
                    >
                      Schedule
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Preview</Typography>
                  <Box sx={{ 
                    bgcolor: '#f5f5f5', 
                    borderRadius: 2, 
                    p: 2,
                    position: 'relative',
                    maxWidth: 300,
                    margin: '0 auto'
                  }}>
                    {formData.image_url && (
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        style={{ width: '100%', borderRadius: 8, marginBottom: 8 }}
                      />
                    )}
                    <Typography variant="subtitle1" fontWeight="bold">
                      {formData.title || 'Notification Title'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {formData.body || 'Your notification message will appear here...'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Target Users Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Filters</Typography>
                  
                  <TextField
                    fullWidth
                    label="Search"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    margin="normal"
                    size="small"
                    placeholder="Name or Phone"
                  />
                  
                  <FormControl fullWidth margin="normal" size="small">
                    <InputLabel>Subscription Status</InputLabel>
                    <Select
                      value={filters.subscription_status}
                      label="Subscription Status"
                      onChange={(e) => setFilters({ ...filters, subscription_status: e.target.value })}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth margin="normal" size="small">
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={filters.gender}
                      label="Gender"
                      onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Typography gutterBottom mt={2}>Age Range</Typography>
                  <Slider
                    value={[filters.age_from, filters.age_to]}
                    onChange={(e, newValue) => setFilters({ 
                      ...filters, 
                      age_from: newValue[0], 
                      age_to: newValue[1] 
                    })}
                    valueLabelDisplay="auto"
                    min={18}
                    max={80}
                  />
                  
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={fetchTargetUsers}
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Find Users'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={9}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      Target Users ({targetCount})
                    </Typography>
                    {formData.target_type === 'selected_users' && (
                      <Chip 
                        label={`${selectedUserIds.length} Selected`} 
                        color="primary" 
                        onDelete={() => setSelectedUserIds([])}
                      />
                    )}
                  </Box>
                  
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {formData.target_type === 'selected_users' && <TableCell>Select</TableCell>}
                          <TableCell>User</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Package</TableCell>
                          <TableCell>Expiry</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {targetUsers.slice(0, 10).map((user) => (
                          <TableRow key={user.id}>
                            {formData.target_type === 'selected_users' && (
                              <TableCell>
                                <Switch
                                  checked={selectedUserIds.includes(user.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedUserIds([...selectedUserIds, user.id]);
                                    } else {
                                      setSelectedUserIds(selectedUserIds.filter(id => id !== user.id));
                                    }
                                  }}
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar>{user.first_name?.charAt(0) || 'U'}</Avatar>
                                <Typography>{user.first_name} {user.last_name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{user.phone}</TableCell>
                            <TableCell>{user.package_name || 'Free'}</TableCell>
                            <TableCell>{user.expiry_date ? moment(user.expiry_date).format('YYYY-MM-DD') : 'N/A'}</TableCell>
                            <TableCell>
                              <Chip 
                                label={user.subscription_status === 'active' ? 'Active' : 'Inactive'}
                                color={user.subscription_status === 'active' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* History Tab */}
        <TabPanel value={tabValue} index={2}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Sent/Success</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notificationHistory.map((notif) => (
                  <TableRow key={notif.id}>
                    <TableCell>{moment(notif.created_at).format('YYYY-MM-DD HH:mm')}</TableCell>
                    <TableCell>{notif.title}</TableCell>
                    <TableCell>
                      <Chip label={notif.notification_type} size="small" />
                    </TableCell>
                    <TableCell>{notif.target_type}</TableCell>
                    <TableCell>{notif.success_count}/{notif.total_targeted}</TableCell>
                    <TableCell>
                      <Chip 
                        label={notif.status} 
                        color={notif.status === 'sent' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>By Notification Type</Typography>
                  {notificationStats?.by_type?.map((type) => (
                    <Box key={type.notification_type} display="flex" justifyContent="space-between" mb={1}>
                      <Typography>{type.notification_type}</Typography>
                      <Typography fontWeight="bold">{type.count}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Recent Activity</Typography>
                  {notificationStats?.recent_activity?.map((activity) => (
                    <Box key={activity.id} mb={2}>
                      <Typography variant="subtitle2">{activity.title}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {moment(activity.created_at).fromNow()} - {activity.success_count} delivered
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Notification</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="datetime-local"
            label="Schedule Date & Time"
            value={formData.schedule_date}
            onChange={(e) => setFormData({ ...formData, schedule_date: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="caption" color="textSecondary">
            Select future date and time to schedule this notification
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleSendNotification(true)} variant="contained" color="primary">
            Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PushNotification;